/**
 * The order of a collection, and where something new goes in it.
 *
 * A collection's order is what a child's tablet shows, top to bottom, and it is
 * the parent's to arrange. Two standing preferences shape it without the parent
 * having to drag anything: whether new episodes arrive at the top or the bottom,
 * and whether long compilations are kept at the end. Both the backend, placing
 * an imported video, and the parent app, re-sorting on request, use these, so
 * the rule is written once.
 */
/**
 * Past this, a "video" on a children's channel is nearly always a compilation:
 * an hour of episodes stitched together. A fair choice for a long car journey
 * and a poor one for a five-minute slot, which is why it can be kept last.
 */
export const LONG_COMPILATION_SECONDS = 20 * 60;
export const isLongCompilation = (durationSeconds) => durationSeconds !== null && durationSeconds > LONG_COMPILATION_SECONDS;
/**
 * Moves long compilations below everything else, keeping each group's order.
 *
 * Stable on purpose: the parent arranged the rest, and a rule about long videos
 * has no business reshuffling the short ones.
 */
export const withLongItemsLast = (order, durationOf) => [
    ...order.filter((id) => !isLongCompilation(durationOf(id))),
    ...order.filter((id) => isLongCompilation(durationOf(id))),
];
/**
 * Where a new item lands, given the collection's preferences.
 *
 * Already present is left where it is: re-importing an episode must not undo
 * the place a parent dragged it to.
 *
 * `after` keeps a batch together. A channel lists its newest video first, and
 * an import that put each one "at the top" in turn would leave the batch upside
 * down; placing each after the one before keeps the listing's order.
 */
export const placeNewItem = (order, item, preferences, durationOf, after) => {
    if (order.includes(item)) {
        return [...order];
    }
    const anchor = after === undefined ? -1 : order.indexOf(after);
    const placed = anchor !== -1
        ? [...order.slice(0, anchor + 1), item, ...order.slice(anchor + 1)]
        : preferences.newItemsFirst
            ? [item, ...order]
            : [...order, item];
    return preferences.longItemsLast ? withLongItemsLast(placed, durationOf) : placed;
};
//# sourceMappingURL=collection-order.js.map